import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { apiService } from './services/api';
import { ThemeProvider } from './contexts/ThemeContext';
import { LocaleProvider } from './contexts/LocaleContext';
import { VersionBadge } from './components/VersionBadge';
import { PreferencesProvider, usePreferences } from './contexts/PreferencesContext';
import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import Biblioteca from './pages/Biblioteca';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import WorkspaceDetail from './pages/WorkspaceDetail';
import WorkshopDetail from './pages/WorkshopDetail';
import Billing from './pages/Billing';
import Observability from './pages/Observability';
import Integrations from './pages/Integrations';
import Plantillas from './pages/Plantillas';
import Finanzas from './pages/Finanzas';
import Soporte from './pages/Soporte';
// Deployed page consolidated into Dashboard hub — legacy import removed.
import PaymentReturn from './pages/PaymentReturn';
import Admin from './pages/Admin';
import Perfil from './pages/Perfil';
import { CuentaPage, SeguridadPage, PreferenciasPage, SoporteUsuarioPage } from './pages/PerfilSubpages';
import IdiomaPage, { AudioPage } from './pages/IdiomaAudio';
import Creditos from './pages/Creditos';
import Aplicaciones from './pages/Aplicaciones';
import WorkspaceUser from './pages/WorkspaceUser';
import ErrorBoundary from './components/ErrorBoundary';
import NexusBackground from './components/brand/NexusBackground';
import { OfflineIndicator } from './components/OfflineIndicator';
import { CreditsProvider } from './contexts/CreditsContext';
import CreditsModalHost from './components/credits/CreditsModalHost';
import { offlineQueue } from './lib/offlineQueue';
import './App.css';

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'hsl(var(--bg))' }}>
        <div className="spinner" style={{ width: '40px', height: '40px' }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

// Sprint Corrección Final: gate para la vista legacy /workspace/:id.
// Solo admins ven WorkspaceDetail con drawer completo; los usuarios
// normales se redirigen automáticamente a la vista moderna /w/:id.
const WorkspaceDetailAdminOnly: React.FC = () => {
  const { user } = useAuth();
  const params = useParams();
  if (user?.role === 'admin') return <WorkspaceDetail />;
  return <Navigate to={`/w/${params.workspaceId || ''}`} replace />;
};

// Public route wrapper (redirect to workspace if already authenticated)
// v1.9 · uses useEffect + imperative navigate so it never races with a
// wrapped page's own post-auth redirect (e.g. AuthPage → /w/{id}).
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectedRef = React.useRef(false);

  React.useEffect(() => {
    if (loading || !isAuthenticated) return;
    // Only redirect if we're STILL on the public route (auth pages).
    if (!location.pathname.startsWith('/auth')) return;
    // Don't step on the callback flow — it navigates on its own.
    if (location.pathname.startsWith('/auth/callback')) return;
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    // v1.11 · Authenticated users hitting /auth are sent to /home (main hub),
    // not to a workspace — workspaces are only created when the user starts
    // a project from the composer.
    navigate('/home', { replace: true });
  }, [loading, isAuthenticated, location.pathname, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'hsl(var(--bg))' }}>
        <div className="spinner" style={{ width: '40px', height: '40px' }} />
      </div>
    );
  }

  return <>{children}</>;
};

function AppContent() {
  return (
    <div className="App">
      <Routes>
        <Route
          path="/auth"
          element={
            <PublicRoute>
              <AuthPage />
            </PublicRoute>
          }
        />
        <Route
          path="/auth/reset"
          element={<AuthPage />}
        />
        <Route
          path="/auth/callback"
          element={<AuthCallback />}
        />
        <Route
          path="/biblioteca"
          element={<ProtectedRoute><Biblioteca /></ProtectedRoute>}
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        {/* Sprint Corrección Final: la vista antigua /workspace/:id
            (con drawer completo Archivos/Deploy/Memoria/Observabilidad)
            solo debe verse en modo admin. Los usuarios normales SIEMPRE
            deben acabar en la vista moderna /w/:id. */}
        <Route
          path="/workspace/:workspaceId"
          element={
            <ProtectedRoute>
              <WorkspaceDetailAdminOnly />
            </ProtectedRoute>
          }
        />
        {/* v1.8 · user-facing clean workspace (ChatGPT-style, no drawer) */}
        <Route
          path="/w/:workspaceId"
          element={
            <ProtectedRoute>
              <WorkspaceUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workshop/:sessionId"
          element={
            <ProtectedRoute>
              <WorkshopDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={<Navigate to="/perfil" replace />}
        />
        <Route
          path="/profile/legacy"
          element={<Navigate to="/perfil" replace />}
        />
        <Route
          path="/settings/billing"
          element={<Navigate to="/creditos" replace />}
        />
        {/* Último Deploy · /billing ya no expone la vista antigua.
            Retorna Stripe sigue vivo en /billing/return. */}
        <Route
          path="/billing/return"
          element={
            <ProtectedRoute>
              <PaymentReturn />
            </ProtectedRoute>
          }
        />
        {/* Último Deploy · unificación de créditos.
            /finanzas ya no expone la vista antigua para usuarios finales:
            redirige a la única fuente de verdad /creditos. Facturación,
            planes y consumo siguen accesibles como sub-tabs de la propia
            página nueva. */}
        <Route path="/finanzas" element={<Navigate to="/creditos" replace />} />
        <Route path="/finanzas/*" element={<Navigate to="/creditos" replace />} />
        <Route path="/billing" element={<Navigate to="/creditos" replace />} />
        {/* v1.8 · dedicated user-facing pages */}
        <Route path="/perfil" element={<ProtectedRoute><Perfil /></ProtectedRoute>} />
        <Route path="/perfil/cuenta" element={<ProtectedRoute><CuentaPage /></ProtectedRoute>} />
        <Route path="/perfil/idioma" element={<ProtectedRoute><IdiomaPage /></ProtectedRoute>} />
        <Route path="/perfil/audio" element={<ProtectedRoute><AudioPage /></ProtectedRoute>} />
        <Route path="/perfil/seguridad" element={<ProtectedRoute><SeguridadPage /></ProtectedRoute>} />
        <Route path="/perfil/preferencias" element={<ProtectedRoute><PreferenciasPage /></ProtectedRoute>} />
        <Route path="/perfil/soporte" element={<ProtectedRoute><SoporteUsuarioPage /></ProtectedRoute>} />
        <Route path="/creditos" element={<ProtectedRoute><Creditos /></ProtectedRoute>} />
        <Route path="/aplicaciones" element={<ProtectedRoute><Aplicaciones /></ProtectedRoute>} />
        <Route path="/cuenta" element={<Navigate to="/perfil/cuenta" replace />} />
        <Route path="/settings" element={<Navigate to="/perfil" replace />} />
        {/* Sprint P0 Quirúrgico Feb 2026 · alias /configuracion → /perfil
            para evitar 404 silenciosos en deep-links. La UX real vive en
            /perfil y sus sub-rutas. */}
        <Route path="/configuracion" element={<Navigate to="/perfil" replace />} />
        <Route path="/configuracion/*" element={<Navigate to="/perfil" replace />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          }
        />
        <Route path="/plantillas" element={
          <ProtectedRoute><Plantillas /></ProtectedRoute>
        } />
        <Route path="/soporte" element={
          <ProtectedRoute><Soporte /></ProtectedRoute>
        } />
        <Route path="/administracion" element={<Navigate to="/admin" replace />} />
        <Route path="/support" element={<Navigate to="/soporte" replace />} />
        <Route
          path="/observability"
          element={
            <ProtectedRoute>
              <Observability />
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations"
          element={
            <ProtectedRoute>
              <Integrations />
            </ProtectedRoute>
          }
        />
        {/* Deprecated routes → redirect to their v1.1 hub replacements. */}
        <Route path="/marketplace" element={<Navigate to="/home" replace />} />
        <Route path="/deployed" element={<Navigate to="/dashboard" replace />} />
        {/* Sprint Corrección Crítica: `/workspaces` eliminado completamente.
            Se redirige a /home para que ningún usuario acceda a la vista legacy. */}
        <Route path="/workspaces" element={<Navigate to="/home" replace />} />
        {/* Root → Home (single-conversation entry point per Feb 2026 redesign) */}
        <Route path="/" element={<Navigate to="/home" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  React.useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => {
          regs.forEach((reg) => {
            reg.unregister().catch(() => {});
          });
        })
        .catch(() => {});
      if ('caches' in window) {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
      }
    }
    // v28 · Start the offline queue flusher on mount.
    offlineQueue.startAutoFlush();
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ThemeProvider>
          <NexusBackground />
          <LocaleProvider>
          <AuthProvider>
            <CreditsProvider>
            <PreferencesProvider>
              <RouteBackgroundLayer />
              <AppContent />
              <VersionBadge />
              <OfflineIndicator />
              <CreditsModalHost />
              <Toaster
                position="bottom-right"
                toastOptions={{
                  style: {
                    background: 'hsl(var(--bg-elev))',
                    border: '1px solid hsl(var(--border))',
                    color: 'hsl(var(--text))',
                  },
                }}
              />
            </PreferencesProvider>
            </CreditsProvider>
          </AuthProvider>
          </LocaleProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

/**
 * RouteBackgroundLayer — renders the user's custom background image
 * for the current route (auth / home / workspace) as a fixed layer
 * BEHIND the NexusBackground so the Nexus signature still shows through
 * when no image is set, and BEHIND the app UI so content stays legible.
 */
const RouteBackgroundLayer: React.FC = () => {
  const { bgFor } = usePreferences();
  const [slot, setSlot] = React.useState<'auth' | 'home' | 'workspace' | null>(null);

  React.useEffect(() => {
    const compute = () => {
      const p = window.location.pathname;
      if (p.startsWith('/auth')) setSlot('auth');
      else if (p.startsWith('/workspace/')) setSlot('workspace');
      else if (p === '/' || p.startsWith('/home')) setSlot('home');
      else setSlot(null);
    };
    compute();
    window.addEventListener('popstate', compute);
    // BrowserRouter uses history.pushState — patch it to notify us
    const origPush = window.history.pushState;
    window.history.pushState = function (...args: any[]) {
      // eslint-disable-next-line prefer-spread
      origPush.apply(window.history, args as any);
      compute();
    };
    return () => {
      window.removeEventListener('popstate', compute);
      window.history.pushState = origPush;
    };
  }, []);

  const url = slot ? bgFor(slot) : undefined;

  // Dim the NexusBackground canvas whenever the user has uploaded a
  // custom background — the user asked for their image to be the
  // protagonist. We toggle a body class the CSS listens for.
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('has-custom-bg', !!url);
    return () => document.body.classList.remove('has-custom-bg');
  }, [url]);

  if (!url) return null;

  return (
    <div
      aria-hidden="true"
      data-testid={`route-bg-${slot}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: -1,
        pointerEvents: 'none',
        backgroundImage: `url("${url}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Subtle overlay for legibility — kept low so the tech
          pattern behind stays visible through glass panels. */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, hsl(var(--bg) / 0.35), hsl(var(--bg) / 0.55))',
      }} />
    </div>
  );
};

export default App;
